import autosize from "autosize";
import { debounce } from "lodash-es";
import { Component, createRef, type ReactElement } from "react";
import Autosuggest from "react-autosuggest";
import ReactDOM from "react-dom";
import { SymbolIcon, TriggerButton } from "widgets";

import { taggedUserIncludes } from "@/app-utils/filter.ts";
import { highlightStartsWith, pluralizeWord } from "@/app-utils/string.ts";
import { type Project, type ProjectActivity, type ProjectMember, type User } from "@/graphql.ts";
import { getProjectLabel } from "@/redux/modules/config.ts";
import { ENTITY_TYPE, FILE_TYPE } from "@/settings/settings.js";

import { PopoverController, TooltipController } from "../core/overlays/components/OverlayControllers.js";
import { Avatar } from "../core/UserAvatar.js";
import { AttachmentSelectPanel, type ISelectAttachmentOption } from "./AttachmentSelectPanel.js";

interface ICommentInputFormProps {
  onPostComment?: (
    comment: string,
    groupIds: number[],
    viewIds: number[],
    flagIds: number[],
    projectFileIds: number[],
  ) => Promise<ProjectActivity>;
  maxChars?: number;
  buttonLabel?: string;
  fieldLabel?: string;
  fieldPlaceholder?: string;
  project: Project;
  projectIsLoading: boolean;
  currentUser?: User;
}

interface ICommentInputFormState {
  comment?: string;
  showAttachmentPopup?: boolean;
  selectedAttachments?: ISelectAttachmentOption[];
  suggestions?: IUserSuggestion[];
}

export interface IUserSuggestion {
  type: SuggestionType;
  user?: User;
}

export enum SuggestionType {
  TagAll = 1,
  TagUser = 2,
}

/**
 * A text field - button combination.
 * Used for posting of comments in the ProjectActivityPanel of a project overview page.
 * Shows the remaining number of characters if maxChars>0.
 *
 * TODO use InputFieldWithUserTagging and consolidate
 *
 * created on 12/1/2016
 */
class CommentInputForm extends Component<ICommentInputFormProps, ICommentInputFormState> {
  private autosuggestRef = createRef<Autosuggest>();
  private textareaRef = createRef<HTMLTextAreaElement>();

  private cursorPosition: number;

  constructor(props: ICommentInputFormProps) {
    super(props);

    this.cursorPosition = 0;

    this.state = {
      comment: "",
      showAttachmentPopup: false,
      selectedAttachments: [],
      suggestions: [],
    };

    this.getSuggestions = this.getSuggestions.bind(this);
    this.getCurrentTagWord = this.getCurrentTagWord.bind(this);
    this.getSuggestionValue = this.getSuggestionValue.bind(this);
    this.renderSuggestion = this.renderSuggestion.bind(this);
    this.renderSuggestionsContainer = this.renderSuggestionsContainer.bind(this);
    this.onSuggestionsClearRequested = this.onSuggestionsClearRequested.bind(this);
    this.onSuggestionsFetchRequested = this.onSuggestionsFetchRequested.bind(this);
    this.onSuggestionSelected = this.onSuggestionSelected.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handlePostComment = this.handlePostComment.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleCursorPositionChange = this.handleCursorPositionChange.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);

    // debouncing is required for closing the attachment panel via click on the paper clip
    this.toggleAttachmentPopup = debounce(this.toggleAttachmentPopup.bind(this), 200);
    this.onAttachmentSelect = this.onAttachmentSelect.bind(this);
    this.onAttachmentClose = this.onAttachmentClose.bind(this);
  }

  handlePostComment(event) {
    const comment = this.state.comment.trim();
    if (comment.length && this.props.onPostComment) {
      const tokens: ISelectAttachmentOption[] = this.state.selectedAttachments;
      let token: ISelectAttachmentOption;
      const groupIds: number[] = [];
      const viewIds: number[] = [];
      const flagIds: number[] = [];
      const projectFileIds: number[] = [];

      for (token of tokens) {
        switch (token.type) {
          case ENTITY_TYPE.GENE:
          case ENTITY_TYPE.TRANSCRIPT:
          case ENTITY_TYPE.PROTEIN:
          case ENTITY_TYPE.ORGANISM:
          case ENTITY_TYPE.MUTATION:
          case ENTITY_TYPE.SAMPLE:
          case ENTITY_TYPE.VCF_VARIANT:
            groupIds.push(Number(token.id));
            break;
          case ENTITY_TYPE.VIEW:
            viewIds.push(Number(token.id));
            break;
          case ENTITY_TYPE.REGION_FLAG:
            flagIds.push(Number(token.id));
            break;
          case FILE_TYPE:
            projectFileIds.push(Number(token.id));
            break;
          default:
            throw Error(`Posting of comment with attachment of type ${token.type} is not supported`);
        }
      }
      this.props.onPostComment(comment, groupIds, viewIds, flagIds, projectFileIds);

      this.setState({
        comment: "",
        selectedAttachments: [],
        suggestions: [],
      });
    }
  }

  toggleAttachmentPopup() {
    this.setState((prevState: ICommentInputFormState) => {
      return { showAttachmentPopup: !prevState.showAttachmentPopup };
    });
  }

  onAttachmentSelect(value) {
    this.setState({ selectedAttachments: value || [] });
  }

  onAttachmentClose() {
    this.toggleAttachmentPopup();
  }

  getSuggestions(token: string): IUserSuggestion[] {
    const suggestions: IUserSuggestion[] = (this.props.project.projectMembers ?? [])
      .filter((m) => {
        // exclude the current user, you cannot suggest to yourself
        return m.user.username !== this.props.currentUser.username;
      })
      .filter((member: ProjectMember) => taggedUserIncludes(member.user, token))
      .map((member: ProjectMember) => {
        return { type: SuggestionType.TagUser, user: member.user };
      });

    // Add @all suggestion at the beginning of list
    if (suggestions.length > 1) {
      suggestions.unshift({ type: SuggestionType.TagAll });
    }

    return suggestions;
  }

  getCurrentTagWord(str, caretPosition) {
    let word;

    const left = str.slice(0, caretPosition).search(/\S+$/);
    const right = str.slice(caretPosition).search(/\s/);

    if (right < 0) {
      word = str.slice(left);
    } else {
      word = str.slice(left, right + caretPosition);
    }

    if (!word || word.trim().length < 1 || word[0] !== "@") {
      return null;
    }

    word = word.trim().toLowerCase().slice(1);

    return word;
  }

  getSuggestionValue(suggestion: IUserSuggestion) {
    return suggestion.type === SuggestionType.TagAll ? "all" : suggestion.user.username;
  }

  renderSuggestion(suggestion: IUserSuggestion, { query }) {
    if (suggestion.type === SuggestionType.TagAll) {
      // Beware: .users contains the current user. too!
      const otherMemberCount =
        (this.props.project.projectMembers ?? []).length > 0 ? (this.props.project.projectMembers ?? []).length - 1 : 0;
      const memberLabel = pluralizeWord("member", otherMemberCount);
      return (
        <span>
          <em>
            Notify all {otherMemberCount} other {getProjectLabel(false, false)} {memberLabel}
          </em>
        </span>
      );
    } else {
      const user: User = suggestion.user;
      const token = this.getCurrentTagWord(query, this.cursorPosition);
      const username: string = user.username ? highlightStartsWith(user.username, token) : "";
      const firstName: string = user.firstName ? highlightStartsWith(user.firstName, token) : "";
      const lastName: string = user.lastName ? highlightStartsWith(user.lastName, token) : "";
      const fullName: string = firstName + " " + lastName;
      const separator: ReactElement =
        user.firstName || user.lastName ? <span className="description">&ndash;</span> : null;

      return (
        <span>
          <div className="thumbnail-wrapper">
            <Avatar user={user} size={24} />
          </div>
          <span dangerouslySetInnerHTML={{ __html: username }} />
          {separator}
          <span dangerouslySetInnerHTML={{ __html: fullName }} className="description" />
        </span>
      );
    }
  }

  onSuggestionsClearRequested() {
    this.setState({ suggestions: [] });
  }

  onSuggestionsFetchRequested({ value }) {
    // TODO better use shouldRenderSuggestions callback
    if (!value?.includes("@")) {
      return;
    }

    const node: Element = ReactDOM.findDOMNode(this.autosuggestRef.current) as Element;
    const textarea = node.querySelector("textarea");

    if (textarea) {
      const token = this.getCurrentTagWord(value, textarea.selectionStart);

      if (token !== null) {
        this.setState({ suggestions: this.getSuggestions(token) });
      } else if (this.state.suggestions.length > 0) {
        this.setState({ suggestions: [] });
      }
    }
  }

  onSuggestionSelected(_event, { suggestionValue }) {
    const node: Element = ReactDOM.findDOMNode(this.autosuggestRef.current) as Element;
    const textarea = node.querySelector("textarea");
    const comment = this.state.comment;
    const cursorPos = textarea.selectionStart;
    const tagStartIndex = comment.lastIndexOf("@", cursorPos) + 1;

    let tagEndIndex = comment.indexOf(" ", tagStartIndex);
    if (tagEndIndex < 0) {
      tagEndIndex = comment.length;
    }

    if (textarea) {
      // add a blank after the suggestion value if there is none present
      const conditionalBlank: string = comment.substr(tagEndIndex, 1) === " " ? "" : " ";
      this.setState({
        comment: [comment.slice(0, tagStartIndex), suggestionValue, conditionalBlank, comment.slice(tagEndIndex)].join(
          "",
        ),
      });
    }
  }

  handleCursorPositionChange(_event) {
    const node: Element = ReactDOM.findDOMNode(this.autosuggestRef.current) as Element;
    const textarea = node.querySelector("textarea");

    if (textarea) {
      this.cursorPosition = textarea.selectionStart;
    }
  }

  handleKeyDown(event) {
    if (event.key === "Enter" && this.state.suggestions.length > 0) {
      event.preventDefault();
    }
  }

  handleChange(event, { newValue, method }) {
    if (this.state.suggestions.length > 0 && (method === "click" || method === "enter")) {
      // User has selected a suggestion via click or enter => delegate to select handler
      this.onSuggestionSelected(event, { suggestionValue: newValue });
      // move focus to textarea again
      this.textareaRef.current.focus();
    } else {
      // normal typing within the textarea
      let text = event.target.value;

      if (this.props.maxChars > 0) {
        text = text.substr(0, this.props.maxChars);
      }

      this.setState({
        comment: text,
      });
    }
  }

  componentDidMount() {
    autosize(this.textareaRef.current);
  }

  renderInputComponent = (inputProps) => {
    const { maxChars, fieldLabel } = this.props;
    const comment: string = this.state.comment;
    const tooltip =
      comment.length === 0
        ? "To post a comment, first enter some text into the comment field"
        : `Post your comment to the ${getProjectLabel(false, false)}`;
    let remainingChars: ReactElement = null;
    if (maxChars > 0 && comment.length > 0) {
      remainingChars = <span className="remaining-chars">{maxChars - comment.length} characters left</span>;
    }

    return (
      <form role="form" onSubmit={this.handlePostComment}>
        <span id="project-activity-comment-input" className="hotspot-beacon" />
        <div className="form-group form-group-default">
          <div className="input-group">
            <label>{fieldLabel} </label>
            {remainingChars}

            <textarea {...inputProps} ref={this.textareaRef} />

            {this.renderAttachmentClip()}

            <span className="input-group-addon submit">
              <TriggerButton
                style={{ height: "64px" }}
                icon={"send"}
                label={this.props.buttonLabel}
                onPress={this.handlePostComment}
                isDisabled={comment.length === 0 || this.props.projectIsLoading}
                tooltip={tooltip}
              >
                {this.props.buttonLabel}
              </TriggerButton>
            </span>
          </div>
        </div>
        {this.props.project && !this.props.project.isPrivateWorkspace && (
          <div className="help" style={{ marginBottom: 0, fontSize: "13px" }}>
            Notify other {getProjectLabel(false, false)} members using @username notation
          </div>
        )}
      </form>
    );
  };

  renderSuggestionsContainer({ children, containerProps }) {
    if (children) {
      return <div {...containerProps}>{children}</div>;
    }
    return null;
  }

  renderAttachmentClip() {
    const attachmentCount = this.state.selectedAttachments.length;

    const attachmentCountFlag: ReactElement =
      attachmentCount > 0 ? (
        <div className="new-notice-count" key={`attach-count-${attachmentCount}`}>
          {attachmentCount}
        </div>
      ) : null;

    // TODO: Use distinct/generic class instead of bell-wrapper?
    return (
      <TooltipController content={"Attach saved items to your comment"}>
        {(tooltipTriggerProps) => (
          <span className="input-group-addon attachment">
            <span className="vert-divider" />

            <PopoverController
              actionWidget={(controllerProps) => (
                <div {...tooltipTriggerProps}>
                  <span className="bell-wrapper" onClick={controllerProps.onPress} ref={controllerProps.ref}>
                    <SymbolIcon icon={"attachment"} />
                    {attachmentCountFlag}
                  </span>
                </div>
              )}
              overlayOptions={{ offset: 15 }}
            >
              {(controllerProps) => (
                <AttachmentSelectPanel
                  {...controllerProps}
                  selectedAttachments={this.state.selectedAttachments}
                  projectId={this.props.project.id}
                  onSelect={this.onAttachmentSelect}
                />
              )}
            </PopoverController>
          </span>
        )}
      </TooltipController>
    );
  }

  render(): ReactElement {
    const inputProps = {
      id: "project-comment-autosuggest",
      value: this.state.comment,
      onChange: this.handleChange,
      onKeyPress: this.handleCursorPositionChange,
      onKeyDown: this.handleKeyDown,
      placeholder: this.props.fieldPlaceholder,
      className: "form-control",
      rows: 2, //  minimun height is 2 rows, set for react-autosize-textarea package (see method renderInputComponent)
      style: { maxHeight: 150 },
    };

    return (
      <div className="comment-input-form">
        <Autosuggest
          ref={this.autosuggestRef}
          suggestions={this.state.suggestions}
          onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
          onSuggestionsClearRequested={this.onSuggestionsClearRequested}
          onSuggestionSelected={this.onSuggestionSelected}
          getSuggestionValue={this.getSuggestionValue}
          renderSuggestion={this.renderSuggestion}
          renderSuggestionsContainer={this.renderSuggestionsContainer}
          renderInputComponent={this.renderInputComponent}
          highlightFirstSuggestion={true}
          inputProps={inputProps}
        />
      </div>
    );
  }
}

export default CommentInputForm;
